import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../core/base.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HeaderService } from '../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../core/security/auth.service';
import { FormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NotificationService } from '../notification/notification.service';
import { CurrentUser } from '../api/models/current-user';
import { ModulesEnum } from '../core/security/modules.enum';
import { RolesEnum } from '../core/security/roles.enum';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { MatDrawer, MatDrawerContainer, MatDrawerMode, MatDrawerContent } from '@angular/material/sidenav';
import { ProPhoneNumberGroupV2Component } from '../pro-messaging/phone-number-group/pro-phone-number-group-v2.component';
import { MessagingGroupModel } from '../api/models/messaging-group-model';
import { MatDialog } from '@angular/material/dialog';
import { GroupWarningDialogComponent } from '../pro-messaging/group-warning-dialog.component';
import { MessagingService } from './messaging.service';
import {
  BillingCycleLimitModel,
  SendMessageModel,
  SmsPhoneNumberModel,
  SmsProcessingPhoneNumberModel,
} from '../api/models';
import { ZendeskService } from '../core/services/zendesk.service';
import { MatAlertHtmlDialogComponent } from '../dialogs/mat-alert-html/mat-alert-dialog.component';
import { ConfirmConversationDeleteDialogComponent } from '../pro-messaging/confirm-conversation-delete/confirm-conversation-delete-dialog.component';
import { MessagesDatasource } from './messages-grid/messages.datasource';
import { AddSmsNumberDialogComponent } from '../phone-numbers/add-sms-number-dialog/add-sms-number-dialog.component';
import { PhoneNumbersService } from '../phone-numbers/phone-numbers.service';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MessagesModule } from 'primeng/messages';
import { PrimeTemplate } from 'primeng/api';
import { MatFormFieldFix } from '../editors/mat-form-field/mat-form-field-fix';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { ExplanationComponent } from '../core/visual/explanation/explanation.component';
import { MatOption } from '@angular/material/core';
import { MatBadge } from '@angular/material/badge';
import { PhoneNumberGroupComponent } from './phone-number-group/phone-number-group.component';
import { ThreadRecipientsComponent } from './thread-recipients/thread-recipients.component';
import { MessagesGridComponent } from './messages-grid/messages-grid.component';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatTooltip } from '@angular/material/tooltip';
import { PhoneNumberPipe } from '../core/pipes/phone-number.pipe';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-messaging',
  templateUrl: './messaging.component.html',
  styleUrls: ['./messaging.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatIcon,
    MatButton,
    NgFor,
    FlexModule,
    MatProgressSpinner,
    MessagesModule,
    MatDrawerContainer,
    MatDrawer,
    MatIconButton,
    MatFormFieldFix,
    MatFormField,
    MatSelect,
    FormsModule,
    MatSelectTrigger,
    ExplanationComponent,
    MatOption,
    MatBadge,
    PhoneNumberGroupComponent,
    MatDrawerContent,
    ReactiveFormsModule,
    NgClass,
    ThreadRecipientsComponent,
    MessagesGridComponent,
    MatInput,
    CdkTextareaAutosize,
    MatTooltip,
    AsyncPipe,
    PhoneNumberPipe,
    Message,
  ],
})
export class MessagingComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly TAG = '[MessagingComponent]';

  @ViewChild('mainContainer') mainContainer: MatDrawerContainer;
  @ViewChild('threadCmp') threadCmp: ProPhoneNumberGroupV2Component;
  @ViewChild('msgHeader') headerElm: ElementRef;
  @ViewChild('msgMenu') msgMenu: ElementRef;
  @ViewChild('textInput') textInput: ElementRef;
  @ViewChild('threadsDrawer') threadsDrawer: MatDrawer;

  staticMessagesSubscription: Subscription;
  newMessageSubscription: Subscription;
  selectedGroup: MessagingGroupModel;
  routedPhoneNumber: string;
  routedThreadKey: string;
  refreshProcessingNumbersTimeout: any;
  smsThreadSortType = 'sortByDateAndUnread';

  form: FormGroup;
  submitting = false;
  readOnlyPhone = true;
  limitsReached = false;
  buttonSubmitPromise: Promise<void>;
  uncompletedMessages: { [number: string]: string } = {};
  hasContactsModule = false;
  hasContactsViewRight = false;
  hasContactsEditRight = false;
  hasPhoneNumbersEditRight = false;
  hasAssignUserAccess = false;
  hasTemplateAccess = false;
  sendAsGroup = false;
  sidenavMode: MatDrawerMode = 'side';
  showThreads = true;
  isMobileView = false;
  mobileViewSubscription: Subscription;
  messagingLimits: BillingCycleLimitModel = {
    limits: 0,
    currentUsage: 0,
    billingCycleStart: '',
    currentBillingCycleStart: '',
    currentBillingCycleEnd: '',
  };
  containerHeight = '100%';
  brandId = '';
  processingNumbers: Array<SmsProcessingPhoneNumberModel> = [];

  private infoIconClickSubscription: Subscription;
  private paramsSubscription: Subscription;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public phoneNumbersService: PhoneNumbersService,
    public notificationService: NotificationService,
    public notificationsService: LocalNotificationsService,
    public zendeskService: ZendeskService,
    public messagesDatasource: MessagesDatasource,
    public dialog: MatDialog,
    private elementRef: ElementRef,
    public messagingService: MessagingService,
    private fb: UntypedFormBuilder,
    private router: Router,
  ) {
    super(route, headerService, titleService, authService);

    this.mobileViewSubscription = this.headerService.isMobileView.subscribe(value => {
      this.isMobileView = value;
      this.showThreads = !value;
      this.sidenavMode = value ? 'over' : 'side';
    });

    this.paramsSubscription = this.route.queryParams.subscribe(async params => {
      this.onRouteParamsChange(params)
        .then(() => {})
        .catch(reason => {
          console.error(reason);
        });
    });

    this.deleteRoles = [RolesEnum.Messaging_Delete];
    this.editRoles = [RolesEnum.Messaging_Edit, RolesEnum.Messaging_Delete];

    this.createForm();
  }

  async onRouteParamsChange(params: Params) {
    console.log(`${this.TAG} onRouteParamsChange`, params);

    this.routedThreadKey = params['thread'];

    if (!this.routedPhoneNumber || this.routedPhoneNumber !== params['tn']) {
      this.routedPhoneNumber = params['tn'];

      await this.selectDefaultPhoneNumber();

      const messagingNumber = this.messagingService.selectedMessagingNumber;
      if (messagingNumber && !messagingNumber.allowSend) {
        this.headerService.addGlobalStaticMessage({
          key: 'sms_read_only_warning',
          severity: 'warn',
          summary: '',
          detail:
            'This number is assigned to another user. If you’d like to allow multiple users to send messages from a single number, upgrade your plan to Pro Business SMS.',
        });
      } else {
        this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
      }

      this.headerService.showInfoIcon = true;
      this.headerService.infoIconTooltip = 'Click for more information';
      this.infoIconClickSubscription = this.headerService.infoIconClick.subscribe(() => {
        console.log('info icon clicked');

        const dialogRef = this.dialog.open(MatAlertHtmlDialogComponent, {
          width: '500px',
          height: '400px',
          data: {
            title: 'Basic SMS',
            actionTitle: 'Go It',
            html: this.headerService.basicPlanDescription(),
            hideCancel: true,
          },
        });

        dialogRef.afterOpened().subscribe(() => {
          const supportLink: HTMLAnchorElement = document
            .getElementsByClassName('open-support-dialog')
            .item(0) as HTMLAnchorElement;
          console.log('supportLink', supportLink);
          if (supportLink) {
            supportLink.onclick = () => {
              dialogRef.close();
              this.zendeskService.show();
              return false;
            };
          }
        });
      });

      this.refreshProcessingNumbers().catch(reason => {
        console.error(reason);
      });
    }

    if (this.routedThreadKey == 'new') {
      this.messagingService.isComposing = true;
      this.selectedGroup = {
        groupKey: '',
        primaryNumber: '',
        unread: 0,
      };
      this.resetForm();
    } else {
      this.messagingService.isComposing = false;
    }
  }

  async selectDefaultPhoneNumber() {
    if (this.routedPhoneNumber) {
      await this.messagingService.messagingPhoneNumbers(this.routedPhoneNumber, true);
    } else {
      await this.messagingService.messagingPhoneNumbers(undefined, true);

      const defaultMessagingNumber = this.messagingService.getMessagingNumber();
      if (defaultMessagingNumber) {
        this.navigateToThread(defaultMessagingNumber).catch(reason => {
          console.error(reason);
        });
      }
    }
  }

  async navigateToThread(tn: string, threadKey?: string) {
    console.log(`${this.TAG} navigateToThread`, tn, threadKey);
    await this.router.navigate([], {
      queryParams: {
        tn: tn,
        thread: threadKey,
      },
    });
  }

  onPhoneNumberValueChange(event) {
    console.log('onPhoneNumberValueChange', event);

    this.navigateToThread(event.phoneNumber).catch(reason => {
      console.error(reason);
    });
  }

  onPhoneNumberClicked(event: SmsPhoneNumberModel) {
    console.log(`${this.TAG} onPhoneNumberClicked`, event);
    this.messagingService.isComposing = false;

    if (event?.campaignId == 'FAKE') {
      this.navigateToThread(undefined).catch(reason => {
        console.error(reason);
      });

      this.addNewSmsNumber();
    } else {
      this.navigateToThread(event.phoneNumber).catch(reason => {
        console.error(reason);
      });
    }
  }

  addNewSmsNumber() {
    const dialogRef = this.dialog.open(AddSmsNumberDialogComponent, {
      width: '600px',
      height: '350px',
      data: {
        smsNumberType: 'sms',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        // refresh phone numbers
        await this.refreshProcessingNumbers();
        await this.selectDefaultPhoneNumber();
      }
    });
  }

  async refreshProcessingNumbers() {
    this.processingNumbers = await this.phoneNumbersService.getSmsProcessingPhoneNumbers({
      smsType: 'sms',
    });

    if (this.processingNumbers.length > 0) {
      clearTimeout(this.refreshProcessingNumbersTimeout);
      this.refreshProcessingNumbersTimeout = setTimeout(async () => {
        await this.refreshProcessingNumbers();
      }, 15000);
    } else {
      await this.selectDefaultPhoneNumber();
    }
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      this.brandId = user.brandId;
      this.smsThreadSortType = user.uiSettings?.smsThreadSortType || 'sortByDateAndUnread';
      this.hasContactsModule = this.authService.userHasPackage([
        ModulesEnum.ContactModule,
        ModulesEnum.PrivateContactModule,
      ]);
      this.hasContactsViewRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_View,
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_View,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);
      this.hasContactsEditRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);

      this.hasPhoneNumbersEditRight = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
      ]);

      this.hasAssignUserAccess = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
        RolesEnum.PhoneNumbers_AssignUser,
      ]);

      this.hasTemplateAccess = this.authService.hasAccess([
        RolesEnum.MessagingTemplateView,
        RolesEnum.MessagingTemplateEdit,
        RolesEnum.MessagingTemplateDelete,
      ]);
    } else {
      this.brandId = '';
      this.hasContactsModule = false;
      this.hasContactsViewRight = false;
      this.hasContactsEditRight = false;
      this.hasPhoneNumbersEditRight = false;
      this.hasTemplateAccess = false;
      this.hasAssignUserAccess = false;
    }
  }

  ngOnInit() {
    console.log(`${this.TAG} ngOnInit`);
    super.ngOnInit();

    this.staticMessagesSubscription = this.headerService.staticMessagesSubject.subscribe(list => {
      if (list && list.length > 0) {
        setTimeout(() => {
          console.log(this.elementRef, this.elementRef.nativeElement);
          const top = this.elementRef?.nativeElement?.offsetTop || 0;
          this.containerHeight = `calc(100% - ${top}px)`;
        }, 100);
      } else {
        this.containerHeight = '100%';
      }
    });

    this.newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      if (message && message.direction === 'out' && message.state === 'sent') {
        // get new billing limits
        await this.updateLimitsMessage();
      }
    });

    this.updateLimitsMessage().then();
  }

  onSelectedThreadChanged(thread: MessagingGroupModel) {
    console.log(`${this.TAG} onSelectedThreadChanged`, thread);
    this.storeTypedMessage();

    if (!this.routedThreadKey || this.routedThreadKey !== thread?.threadKey) {
      this.router
        .navigate([], { queryParams: { tn: this.routedPhoneNumber, thread: thread?.threadKey } })
        .catch(error => console.log(error));
    }

    this.updateMessagingForm(thread);
  }

  updateMessagingForm(thread: MessagingGroupModel) {
    console.log(`${this.TAG} updateMessagingForm`, thread);
    this.selectedGroup = thread;

    if (this.isMobileView) {
      this.threadsDrawer?.close().then();
    }

    this.resetForm();
  }

  async updateLimitsMessage() {
    console.log('updateLimitsMessage');
    this.messagingLimits = await this.messagingService.getMessagingLimits();
    if (this.messagingLimits.limits > 0) {
      this.limitsReached = this.messagingLimits.currentUsage >= this.messagingLimits.limits;

      const remainCount = this.messagingLimits.limits - this.messagingLimits.currentUsage;
      const limit10Percent = this.messagingLimits.limits * 0.1;

      if (limit10Percent >= remainCount) {
        this.headerService.addGlobalStaticMessage({
          key: 'sms_limit_warning',
          severity: 'warn',
          summary: 'Warning!',
          detail: `You ${this.limitsReached ? 'have reached' : 'are approaching'} your monthly outgoing SMS message limit for your Basic SMS account.
          You have <b>${remainCount}</b> outgoing messages remaining out of <b>${this.messagingLimits.limits}</b>.
          <a href="javascript:void(0);" class="open-support"><b>Upgrade to Pro</b></a>`,
        });
        setTimeout(() => {
          const supportLink: HTMLAnchorElement = document
            .getElementsByClassName('open-support')
            .item(0) as HTMLAnchorElement;
          console.log('supportLink', supportLink);
          if (supportLink) {
            supportLink.onclick = () => {
              this.zendeskService.show();
              return false;
            };
          }
        });
      } else {
        this.headerService.removeGlobalStaticMessage('sms_limit_warning');
        this.limitsReached = false;
      }
    } else {
      this.headerService.removeGlobalStaticMessage('sms_limit_warning');
      this.limitsReached = false;
    }
  }

  storeTypedMessage() {
    if (this.selectedGroup?.threadKey && this.form.getRawValue().message) {
      const key = `${this.routedPhoneNumber}|${this.selectedGroup?.threadKey}`;
      this.uncompletedMessages[key] = this.form.getRawValue().message;
    }
  }

  clearTypedMessage() {
    if (this.selectedGroup?.threadKey) {
      const key = `${this.messagingService.getMessagingNumber()}|${this.selectedGroup.groupKey}`;
      this.uncompletedMessages[key] = undefined;
    }
  }

  resetForm() {
    const selectedGroup = this.selectedGroup;

    this.readOnlyPhone = selectedGroup && !!selectedGroup.groupKey;

    const clientNumbers = [];
    let uncompletedMessage = '';
    if (selectedGroup && selectedGroup.contacts) {
      for (const contact of selectedGroup.contacts) {
        clientNumbers.push({
          phoneNumber: contact.phoneNumber,
          name: contact.name || '',
          type: 'Contacts',
          uniqueKey: `${contact.phoneNumber || ''}|Contacts||''}`,
        });
      }
      const key = `${this.messagingService.getMessagingNumber()}|${this.selectedGroup.groupKey}`;
      uncompletedMessage = this.uncompletedMessages[key] || '';
    }

    this.form.reset({
      primaryPhoneNumber: this.messagingService.getMessagingNumber(),
      clientPhoneNumbers: clientNumbers,
      message: uncompletedMessage,
    });
  }

  ngOnDestroy() {
    this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
    this.headerService.removeGlobalStaticMessage('sms_limit_warning');
    this.newMessageSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
    this.staticMessagesSubscription?.unsubscribe();
    this.messagingService.messagingNumbers = [];

    this.headerService.showInfoIcon = false;
    this.infoIconClickSubscription?.unsubscribe();
    this.mobileViewSubscription?.unsubscribe();

    this.messagingService.isComposing = false;

    clearTimeout(this.refreshProcessingNumbersTimeout);

    super.ngOnDestroy();
  }

  async markUnreadMessages() {
    console.log('mark unread messages');
    const group = this.selectedGroup;

    if (group && group.unread > 0) {
      this.messagingService.markUnreadMessagesByThreadKey(group.threadKey).catch(e => console.log(e));
    }
  }

  msgKeyPress(event) {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      this.onSubmit().catch(error => console.error(error));
    }
  }

  onNewMessage(event) {
    event.preventDefault();

    this.messagingService.isComposing = true;
    this.navigateToThread(this.routedPhoneNumber, 'new').catch(reason => {
      console.error(reason);
    });
  }

  async onSubmit() {
    console.log(`${this.TAG} form submit: ${this.form.valid}`);
    if (this.form.valid && !this.submitting) {
      if (!this.readOnlyPhone) {
        const reqData = this.prepareFormModel();
        if (reqData.toBulk.length > 1) {
          const dialogRef = this.dialog.open(GroupWarningDialogComponent);

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              this.sendAsGroup = 'group' === result;
              this.submitting = true;
              this.buttonSubmitPromise = this.processSubmit();
            }
          });
        } else {
          this.submitting = true;
          this.buttonSubmitPromise = this.processSubmit();
        }
      } else {
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      }
    }
  }

  async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();

    if (!reqData.text) {
      this.submitting = false;
      this.notificationService.notifyDanger('Message is empty');
      return;
    }

    try {
      const sentMessage = await this.messagingService.sendMessage(reqData);
      if (sentMessage) {
        this.clearTypedMessage();
        this.messagingService.isComposing = false;
        this.resetForm();

        setTimeout(() => {
          this.navigateToThread(this.routedPhoneNumber, sentMessage.groupKey);
        });
      }
    } catch (e) {
      console.error('unable to send message: ', e);
    } finally {
      this.submitting = false;
    }
  }

  prepareFormModel(): SendMessageModel {
    const formModel = this.form.value;

    return {
      from: formModel.primaryPhoneNumber,
      toBulk: formModel.clientPhoneNumbers,
      text: formModel.message,
    };
  }

  createForm(): void {
    this.form = this.fb.group({
      primaryPhoneNumber: ['', Validators.required],
      clientPhoneNumbers: [[], [Validators.required]],
      message: ['', [Validators.maxLength(2048)]],
    });
  }

  onDeleteClick() {
    const dialogRef = this.dialog.open(ConfirmConversationDeleteDialogComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onDeleteConversationProcess(result === 2).catch(error => console.error(error));
      }
    });
  }

  async onDeleteConversationProcess(block: boolean): Promise<void> {
    await this.messagingService.deleteFullConversation(this.selectedGroup.groupKey, block);
    this.notificationService.notifySuccess('Conversation has been deleted');

    await this.navigateToThread(this.routedPhoneNumber);
  }

  openSupportWidget() {
    this.zendeskService.toggle();
  }
}
